import { useState, useMemo } from "react";

export default function ReportCard({ title, subtitle, columns, data, loading, periods, onPeriodChange, displayFooter }) {
    const [selectedPeriod, setSelectedPeriod] = useState("6m");

    const totals = useMemo(() => {
        return {
            Volume: data.reduce(
                (total, row) => total + (Number(row.Volume) || 0),
                0
            ),
            Count: data.reduce(
                (total, row) => total + (Number(row.Count) || 0),
                0
            )
        };
    }, [data]);

    const dataWithShare = useMemo(() => {
        return data.map((row) => ({
            ...row,
            share:
                totals.Volume > 0
                    ? ((Number(row.Volume) || 0) / totals.Volume) * 100
                    : 0
        }));
    }, [data, totals.Volume]);

    return (
        <div className="report-card max-h-150">
            <div className="report-card-header">
                <div>
                    <h3>{title}</h3>

                    {subtitle && (
                        <p>{subtitle}</p>
                    )}
                </div>

                {periods && (
                    <div className="report-periods">
                        {periods.map((period) => (
                            <button
                                key={period}
                                type="button"
                                className={
                                    selectedPeriod === period
                                        ? "report-period uppercase active"
                                        : "report-period uppercase"
                                }
                                onClick={async () => {
                                    setSelectedPeriod(period);
                                    await onPeriodChange?.(period);
                                }}
                            >
                                {period}
                            </button>
                        ))}
                    </div>
                )}
            </div>

            <div className="report-table-wrapper">
                {loading ? (
                    <div className="report-loading">
                        Loading...
                    </div>
                ) : (
                    <table className="report-table">
                        <thead>
                            <tr>
                                {columns.map((column) => (
                                    <th
                                        key={column.key}
                                        className={column.align || ""}
                                    >
                                        {column.label}
                                    </th>
                                ))}
                            </tr>
                        </thead>

                        <tbody>
                            {dataWithShare.map((row, rowIndex) => (
                                <tr key={row.id ?? rowIndex}>
                                    {columns.map((column) => {
                                        const value = row[column.key];

                                        if (
                                            column.type === "bar" &&
                                            column.key === "share"
                                        ) {
                                            return (
                                                <td key={column.key}>
                                                    <div className="report-bar-cell">
                                                        <span>
                                                            {value.toFixed(1)}%
                                                        </span>

                                                        <div className="report-bar">
                                                            <div
                                                                className="report-bar-fill"
                                                                style={{
                                                                    width: `${Math.min(
                                                                        value,
                                                                        100
                                                                    )}%`
                                                                }}
                                                            />
                                                        </div>
                                                    </div>
                                                </td>
                                            );
                                        }

                                        if (column.type === "bar") {
                                            return (
                                                <td key={column.key}>
                                                    <div className="report-bar-cell">
                                                        <span>{value}</span>

                                                        <div className="report-bar">
                                                            <div
                                                                className="report-bar-fill"
                                                                style={{
                                                                    width: `${Math.min(
                                                                        Number(value) || 0,
                                                                        100
                                                                    )}%`
                                                                }}
                                                            />
                                                        </div>
                                                    </div>
                                                </td>
                                            );
                                        }

                                        return (
                                            <td
                                                key={column.key}
                                                className={column.align || ""}
                                            >
                                                {column.render
                                                    ? column.render(value, row)
                                                    : value}
                                            </td>
                                        );
                                    })}
                                </tr>
                            ))}
                        </tbody>
                    </table>
                )}
            </div>
            {displayFooter &&
                <div className="report-table-total">
                    <table className="report-table">
                        {!loading &&
                            <tbody>
                                <tr>
                                    {columns.map((column, index) => {
                                        if (index === 0) {
                                            return (
                                                <td
                                                    key={column.key}
                                                    className={column.align || ""}
                                                >
                                                    Total (<span className="uppercase">{selectedPeriod}</span>)
                                                </td>
                                            );
                                        }

                                        if (
                                            column.type === "bar" &&
                                            column.key === "share"
                                        ) {
                                            return (
                                                <td key={column.key}>
                                                    <div className="report-bar-cell">
                                                        <span>100.0%</span>

                                                        <div className="report-bar">
                                                            <div
                                                                className="report-bar-fill"
                                                                style={{
                                                                    width: "100%"
                                                                }}
                                                            />
                                                        </div>
                                                    </div>
                                                </td>
                                            );
                                        }

                                        const value = totals[column.key];

                                        return (
                                            <td
                                                key={column.key}
                                                className={column.align || ""}
                                            >
                                                {column.render
                                                    ? column.render(value, totals)
                                                    : value}
                                            </td>
                                        );
                                    })}
                                </tr>
                            </tbody>
                        }
                    </table>
                </div>
            }
        </div>
    );
}